import React, { useCallback, useRef } from 'react';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Link from '@tiptap/extension-link';
import Image from '@tiptap/extension-image';
import Placeholder from '@tiptap/extension-placeholder';
import TextAlign from '@tiptap/extension-text-align';
import Youtube from '@tiptap/extension-youtube';
import {
  Bold, Italic, Strikethrough, Heading2, Heading3, List, ListOrdered,
  Quote, Code, Link2, Link2Off, Image as ImageIcon, Youtube as YoutubeIcon,
  Undo, Redo, AlignLeft, AlignCenter, AlignRight, Minus
} from 'lucide-react';
import { uploadImage } from '../lib/api';
import { toast } from 'sonner';

const Btn = ({ onClick, active, disabled, title, testid, children }) => (
  <button type="button" onClick={onClick} disabled={disabled} title={title} data-testid={testid}
    className={`h-8 min-w-[32px] px-2 flex items-center justify-center text-[11px] font-black transition-colors border-r border-[#0B1F40]/10 ${active ? 'bg-[#0B1F40] text-white' : 'text-[#0B1F40]/60 hover:bg-[#F4F5F7]'} disabled:opacity-30 disabled:cursor-not-allowed`}>
    {children}
  </button>
);

export default function RichTextEditor({ value, onChange, placeholder = 'Escribe el contenido…', testid = 'rich-editor' }) {
  const imageInputRef = useRef(null);

  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        heading: { levels: [2, 3] },
        horizontalRule: { HTMLAttributes: { class: 'my-6 border-t-2 border-[#0B1F40]/20' } },
        blockquote: { HTMLAttributes: { class: 'border-l-4 border-[#E50B15] pl-4 italic text-[#0B1F40]/80 my-4' } },
      }),
      Link.configure({ openOnClick: false, HTMLAttributes: { class: 'text-[#E50B15] underline hover:text-[#0B1F40]' } }),
      Image.configure({ HTMLAttributes: { class: 'my-4 max-w-full border border-[#0B1F40]/20' } }),
      Placeholder.configure({ placeholder }),
      TextAlign.configure({ types: ['heading', 'paragraph'] }),
      Youtube.configure({ HTMLAttributes: { class: 'my-4 aspect-video w-full' }, width: 640, height: 360, controls: true }),
    ],
    content: value || '',
    onUpdate: ({ editor }) => onChange?.(editor.getHTML()),
    editorProps: {
      attributes: {
        class: 'prose prose-sm max-w-none min-h-[260px] px-4 py-3 focus:outline-none text-[#0B1F40] leading-relaxed',
      },
    },
  });

  // Keep external value in sync (e.g. on edit page load)
  React.useEffect(() => {
    if (!editor) return;
    const current = editor.getHTML();
    if (value && value !== current) editor.commands.setContent(value, false);
  }, [value, editor]);

  const setLink = useCallback(() => {
    if (!editor) return;
    const prev = editor.getAttributes('link').href;
    const url = window.prompt('URL del enlace:', prev || 'https://');
    if (url === null) return;
    if (url === '') { editor.chain().focus().extendMarkRange('link').unsetLink().run(); return; }
    editor.chain().focus().extendMarkRange('link').setLink({ href: url, target: '_blank', rel: 'noopener noreferrer' }).run();
  }, [editor]);

  const addYoutube = useCallback(() => {
    if (!editor) return;
    const url = window.prompt('URL de YouTube:', 'https://www.youtube.com/watch?v=');
    if (!url) return;
    editor.chain().focus().setYoutubeVideo({ src: url }).run();
  }, [editor]);

  const addImage = useCallback(() => imageInputRef.current?.click(), []);

  const handleImageFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !editor) return;
    try {
      const { data } = await uploadImage(file);
      const API = (process.env.REACT_APP_BACKEND_URL || '') + '/api';
      const url = `${API}/files/${data.path}`;
      editor.chain().focus().setImage({ src: url, alt: file.name }).run();
      toast.success('Imagen insertada');
    } catch {
      toast.error('Error al subir imagen');
    }
    e.target.value = '';
  };

  if (!editor) return null;

  return (
    <div data-testid={testid} className="border-2 border-[#0B1F40]/20 focus-within:border-[#E50B15] bg-white">
      {/* Toolbar */}
      <div className="flex flex-wrap items-stretch border-b-2 border-[#0B1F40]/10 bg-[#FAFAFA]">
        <Btn testid="editor-bold" onClick={() => editor.chain().focus().toggleBold().run()} active={editor.isActive('bold')} title="Negrita (Ctrl+B)"><Bold className="h-3.5 w-3.5" /></Btn>
        <Btn testid="editor-italic" onClick={() => editor.chain().focus().toggleItalic().run()} active={editor.isActive('italic')} title="Cursiva (Ctrl+I)"><Italic className="h-3.5 w-3.5" /></Btn>
        <Btn testid="editor-strike" onClick={() => editor.chain().focus().toggleStrike().run()} active={editor.isActive('strike')} title="Tachado"><Strikethrough className="h-3.5 w-3.5" /></Btn>
        <Btn testid="editor-code" onClick={() => editor.chain().focus().toggleCode().run()} active={editor.isActive('code')} title="Código en línea"><Code className="h-3.5 w-3.5" /></Btn>
        <span className="w-px bg-[#0B1F40]/10" />
        <Btn testid="editor-h2" onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()} active={editor.isActive('heading', { level: 2 })} title="Subtítulo grande"><Heading2 className="h-3.5 w-3.5" /></Btn>
        <Btn testid="editor-h3" onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()} active={editor.isActive('heading', { level: 3 })} title="Subtítulo chico"><Heading3 className="h-3.5 w-3.5" /></Btn>
        <Btn testid="editor-ul" onClick={() => editor.chain().focus().toggleBulletList().run()} active={editor.isActive('bulletList')} title="Lista con viñetas"><List className="h-3.5 w-3.5" /></Btn>
        <Btn testid="editor-ol" onClick={() => editor.chain().focus().toggleOrderedList().run()} active={editor.isActive('orderedList')} title="Lista numerada"><ListOrdered className="h-3.5 w-3.5" /></Btn>
        <Btn testid="editor-quote" onClick={() => editor.chain().focus().toggleBlockquote().run()} active={editor.isActive('blockquote')} title="Cita"><Quote className="h-3.5 w-3.5" /></Btn>
        <Btn testid="editor-hr" onClick={() => editor.chain().focus().setHorizontalRule().run()} title="Separador"><Minus className="h-3.5 w-3.5" /></Btn>
        <span className="w-px bg-[#0B1F40]/10" />
        <Btn testid="editor-align-left" onClick={() => editor.chain().focus().setTextAlign('left').run()} active={editor.isActive({ textAlign: 'left' })} title="Alinear izquierda"><AlignLeft className="h-3.5 w-3.5" /></Btn>
        <Btn testid="editor-align-center" onClick={() => editor.chain().focus().setTextAlign('center').run()} active={editor.isActive({ textAlign: 'center' })} title="Centrar"><AlignCenter className="h-3.5 w-3.5" /></Btn>
        <Btn testid="editor-align-right" onClick={() => editor.chain().focus().setTextAlign('right').run()} active={editor.isActive({ textAlign: 'right' })} title="Alinear derecha"><AlignRight className="h-3.5 w-3.5" /></Btn>
        <span className="w-px bg-[#0B1F40]/10" />
        <Btn testid="editor-link" onClick={setLink} active={editor.isActive('link')} title="Enlace"><Link2 className="h-3.5 w-3.5" /></Btn>
        {editor.isActive('link') && (
          <Btn testid="editor-unlink" onClick={() => editor.chain().focus().unsetLink().run()} title="Quitar enlace"><Link2Off className="h-3.5 w-3.5" /></Btn>
        )}
        <Btn testid="editor-image" onClick={addImage} title="Insertar imagen"><ImageIcon className="h-3.5 w-3.5" /></Btn>
        <Btn testid="editor-youtube" onClick={addYoutube} title="Insertar video de YouTube"><YoutubeIcon className="h-3.5 w-3.5" /></Btn>
        <span className="w-px bg-[#0B1F40]/10 flex-1" />
        <Btn testid="editor-undo" onClick={() => editor.chain().focus().undo().run()} disabled={!editor.can().undo()} title="Deshacer"><Undo className="h-3.5 w-3.5" /></Btn>
        <Btn testid="editor-redo" onClick={() => editor.chain().focus().redo().run()} disabled={!editor.can().redo()} title="Rehacer"><Redo className="h-3.5 w-3.5" /></Btn>
      </div>

      <input ref={imageInputRef} type="file" accept="image/*" onChange={handleImageFile} className="hidden" />

      {/* Content */}
      <EditorContent editor={editor} />
    </div>
  );
}
